/**
 * AivaBubble.tsx — the AiVA message shell used by the Slide Transform chat flow.
 *
 * Presentation only: the mockup's persona block (avatar + "AiVA · just now"
 * head + tinted bubble + dashed divider + white action pills) with no knowledge
 * of the transform itself. The flow composes these; the transform logic lives
 * in the hooks.
 *
 * These are LOCAL to the transform flow rather than `ChatMessage`s in the
 * server-backed thread: the flow's steps are client-side (read the deck, call
 * DeckorateAPI, write slides back), carry live-updating progress, and must not
 * be persisted to or reconciled against a chat thread the backend owns.
 */

import type { ReactNode } from "react";
import styles from "./AivaBubble.module.css";

/** The sparkle mark the mockup uses for AiVA. */
function SparkleIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10 2l1.5 4.5L16 8l-4.5 1.5L10 14l-1.5-4.5L4 8l4.5-1.5z" />
    </svg>
  );
}

export interface AivaPill {
  readonly label: string;
  readonly onClick: () => void;
  readonly disabled?: boolean;
  /** Renders as the filled brand button — at most one per row. */
  readonly primary?: boolean;
  readonly title?: string;
}

export interface AivaBubbleProps {
  readonly children: ReactNode;
  readonly pills?: readonly AivaPill[];
  /** Italic "Working on it…" style line shown above the bubble. */
  readonly working?: string;
  /** Right-hand side of the head, after "AiVA ·". Defaults to "just now". */
  readonly meta?: string;
}

export function AivaBubble({ children, pills, working, meta = "just now" }: Readonly<AivaBubbleProps>) {
  return (
    <div className={styles.message}>
      <div className={styles.head}>
        <span className={styles.avatar}>
          <SparkleIcon />
        </span>
        <strong>AiVA</strong>
        <span className={styles.timestamp}>· {meta}</span>
      </div>

      {working ? <p className={styles.working}>{working}</p> : null}

      <div className={styles.bubble}>{children}</div>

      {pills && pills.length > 0 ? (
        <>
          <div className={styles.divider} />
          <div className={styles.pills}>
            {pills.map((pill) => (
              <button
                key={pill.label}
                type="button"
                className={`${styles.pill} ${pill.primary ? styles.pillPrimary : ""}`}
                onClick={pill.onClick}
                disabled={pill.disabled}
                {...(pill.title ? { title: pill.title } : {})}
              >
                {pill.label}
              </button>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

/** The user's own message, echoed into the flow when they pick an action. */
export function UserBubble({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <div className={styles.userMessage}>
      <div className={styles.userBubble}>{children}</div>
    </div>
  );
}
